import { Fragment } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { AutoAwesome, Close as CloseIcon, Palette, Public, Storefront } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

import { RightPanelOverlayModal, useModalDismissedState } from 'app/shared-components/modals';
import { PrimaryButton, TransparentButton } from 'app/shared-components/buttons';
import FeatureCard from 'app/shared-components/FeatureCard';
import useEventTracker from 'app/hooks/useEventTracker';
import useHasPlusFeature from 'app/hooks/useHasPlusFeature';
import useLoggedInUser from 'app/hooks/useLoggedInUser';
import useOrganisationFeatures from 'app/hooks/useOrganisationFeatures';
import useSelectedPharmacy from 'app/hooks/useSelectedPharmacy';
import { UnlockWithPlusChip } from 'app/shared-components/PlusPaywall';
import { WEBSITE_GUIDE_EVENTS } from 'app/tracking/constants';

import HubSpotVideo from './HubSpotVideo';

const WEB_BUILDER_PROMO_DISMISSED_KEY = 'web_builder_promo_dismissed';

/**
 * Dismissal is remembered per user so that on a shared browser (pharmacy PCs, support switching
 * accounts) one person's "Not now" doesn't hide the introduction from everyone else.
 */
export function webBuilderPromoDismissedKey(userId: string) {
  return `${WEB_BUILDER_PROMO_DISMISSED_KEY}-${userId}`;
}
const WEBSITE_BUILDER_PATH = '/plus-websites';
const ONBOARDING_PATH = `${WEBSITE_BUILDER_PATH}/onboarding`;

const FEATURES = [
  {
    icon: Palette,
    title: 'Pick a Look',
    description: 'Choose a theme, add your logo and brand colours in a few clicks.',
  },
  {
    icon: Storefront,
    title: 'Add Your Services',
    description: 'Services and products flow straight from Connect, always up to date.',
  },
  {
    icon: Public,
    title: 'Go Live',
    description: 'Publish on your own domain in minutes. No developer needed.',
  },
];

/**
 * "What's new" modal introducing the Website builder with its guide video. Shown once to every
 * logged-in user the first time they open Connect; dismissing it (close, "Not now" or the CTA)
 * remembers the choice in localStorage under a per-user key.
 */
export default function WebBuilderPromoModal(): JSX.Element | null {
  const user = useLoggedInUser();
  const navigate = useNavigate();
  const trackEvent = useEventTracker();
  const { pricingLaunched } = useOrganisationFeatures();
  const {
    hasAccess: hasWebBuilder,
    isHidden: webBuilderHidden,
    isLoading: webBuilderLoading,
  } = useHasPlusFeature('webBuilderAccessEnabled', false);
  const organisationId = useSelectedPharmacy()?.chain_id;
  const { showOnboarding, dismiss } = useModalDismissedState(
    webBuilderPromoDismissedKey(user?._id ?? 'guest')
  );

  // Unlike the gates, a plan that cannot be loaded still sees the promo, as today.
  if (!user || !showOnboarding || webBuilderLoading || hasWebBuilder || webBuilderHidden) {
    return null;
  }

  const handleDismiss = () => {
    trackEvent(WEBSITE_GUIDE_EVENTS.PROMO_DISMISSED.action, WEBSITE_GUIDE_EVENTS.PROMO_DISMISSED);
    dismiss();
  };

  const handleTryIt = () => {
    trackEvent(
      WEBSITE_GUIDE_EVENTS.PROMO_CTA_CLICKED.action,
      WEBSITE_GUIDE_EVENTS.PROMO_CTA_CLICKED
    );
    dismiss();
    // Without a selected organisation the entry route lets the organisation selector pick one.
    navigate(
      organisationId
        ? { pathname: ONBOARDING_PATH, search: `?organisationId=${organisationId}` }
        : WEBSITE_BUILDER_PATH
    );
  };

  return (
    <RightPanelOverlayModal open onClose={handleDismiss}>
      <Box className="vtwo:bg-white vtwo:rounded-xl vtwo:flex vtwo:flex-col vtwo:w-[680px] vtwo:px-5 vtwo:py-4 vtwo:shadow-lg vtwo:outline-none">
        <Box className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:mb-4">
          <Box className="vtwo:flex vtwo:items-center vtwo:gap-3">
            <Typography className="vtwo:font-bold vtwo:text-lg vtwo:text-typography-primary">
              What's new
            </Typography>
            {pricingLaunched && <UnlockWithPlusChip />}
          </Box>
          <IconButton
            onClick={handleDismiss}
            size="small"
            className="vtwo:text-neutral-500"
            aria-label="Close Website builder promotion"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        <Typography className="vtwo:font-bold vtwo:text-xl vtwo:text-typography-primary vtwo:text-center vtwo:mb-2">
          Build Your Pharmacy Website in Minutes
        </Typography>

        <Typography className="vtwo:text-sm vtwo:text-typography-secondary vtwo:text-center vtwo:mb-4 vtwo:leading-[22px]">
          Design, publish and update your website straight from Connect.
        </Typography>

        <Box className="vtwo:rounded-xl vtwo:overflow-hidden vtwo:bg-neutral-100 vtwo:mx-auto vtwo:mb-5 vtwo:w-full vtwo:max-w-[520px]">
          <HubSpotVideo />
        </Box>

        <Box className="vtwo:flex vtwo:gap-4 vtwo:mb-6">
          {FEATURES.map((feature, index) => (
            <Fragment key={feature.title}>
              <FeatureCard
                icon={feature.icon}
                title={feature.title}
                description={feature.description}
              />
              {index < FEATURES.length - 1 && <Divider orientation="vertical" flexItem />}
            </Fragment>
          ))}
        </Box>

        <Box className="vtwo:flex vtwo:items-center vtwo:justify-end vtwo:gap-3">
          <TransparentButton label="Not now" cb={handleDismiss} size="m" outlined />
          <PrimaryButton
            label="Try Website builder"
            icon={<AutoAwesome />}
            cb={handleTryIt}
            size="m"
            variant="featured"
          />
        </Box>
      </Box>
    </RightPanelOverlayModal>
  );
}
